// The shared inbox: every WhatsApp conversation, and who is answering it.
//
// Mounted from AppMain when activeModule === 'inboxConversations'.
//
// WHAT THIS SCREEN IS FOR
// ----------------------
// One question, answered at a glance for every open thread: is a machine or
// a person talking to this customer right now? Everything else on the screen
// is secondary to that, which is why "who is answering" is a coloured band
// across the top of the transcript rather than a field in a details panel.
//
// WHY THE COMPOSER IS ALWAYS AVAILABLE
// ------------------------------------
// A rep can type into any open thread without first pressing "take over".
// Sending IS the takeover, and the banner says so before they send. Making
// them click twice would leave the agent free to reply in the gap between
// the two clicks — and the customer lives in that gap.
//
// THE 24-HOUR NOTICE IS NOT OURS
// ------------------------------
// WhatsApp only carries a free-form reply within 24 hours of the customer's
// own last message. Outside that, Meta rejects it whatever we do. The window
// is shown before the rep types, not discovered after their message quietly
// fails to arrive.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: radius.sm, fontSize: 13.5,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
};

function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
}

// Who holds the thread, said in words rather than a status code.
function HandlingBand({ conv }) {
  const human = conv.handling === 'HUMAN';
  const paused = conv.handling === 'PAUSED';
  const tone = human ? color.success : paused ? color.warning : color.medical;
  const text = human
    ? `${conv.assigned_to_name} is answering this conversation. The agent will not reply.`
    : paused
      ? 'Waiting for a person. Nothing is being sent automatically.'
      : 'The AI agent is answering this conversation.';
  return (
    <div style={{
      background: `${tone}14`, borderLeft: `3px solid ${tone}`,
      padding: `${space(1)} ${space(1.5)}`, borderRadius: radius.sm,
      fontSize: 13, color: color.text, marginBottom: space(1.5),
    }}>{text}</div>
  );
}

function Bubble({ m }) {
  const inbound = m.direction === 'INBOUND';
  const bg = inbound ? '#fff'
    : m.author_kind === 'AI' ? `${color.medical}12` : `${color.success}12`;
  const who = inbound ? 'Customer'
    : m.author_kind === 'AI' ? 'AI agent'
      : m.author_kind === 'STAFF' ? (m.author_name || 'Staff') : 'System';
  return (
    <div style={{
      display: 'flex',
      justifyContent: inbound ? 'flex-start' : 'flex-end',
      marginBottom: space(1),
    }}>
      <div style={{
        maxWidth: '78%', background: bg, padding: `${space(1)} ${space(1.5)}`,
        borderRadius: radius.md, border: `1px solid ${color.border}`,
      }}>
        <div style={{
          fontSize: 11, color: color.textSecondary, marginBottom: 3,
          display: 'flex', gap: space(1), alignItems: 'baseline',
        }}>
          <strong>{who}</strong>
          <span>{when(m.sent_at || m.created_at)}</span>
        </div>
        <div style={{ fontSize: 13.5, color: color.text, whiteSpace: 'pre-wrap' }}>
          {m.body}
        </div>
        {m.failed_reason && (
          <div style={{ fontSize: 11.5, color: color.danger, marginTop: 4 }}>
            Not delivered: {m.failed_reason}
          </div>
        )}
      </div>
    </div>
  );
}

function Thread({ id, onChanged, notify }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setErr('');
    try { setData(await req(`/api/conversations/${id}`)); }
    catch (e) { setErr(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [data]);

  const act = async (path, payload) => {
    setBusy(true);
    try {
      const r = await req(`/api/conversations/${id}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
      });
      if (r.detail) notify?.(r.detail, 'success');
      await load();
      onChanged?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!body.trim()) return;
    const text = body;
    setBody('');
    await act('send', { body: text });
  };

  if (err) return <ErrorBox msg={err} />;
  if (!data) return <SkeletonCards n={2} />;

  const c = data.conversation;
  const willTakeOver = c.handling !== 'HUMAN';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: space(1) }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: space(1),
        }}>
          <div>
            <div style={{ fontWeight: 650, fontSize: 15, color: color.text }}>
              {c.customer_name || c.display_name || c.peer_address}
            </div>
            <div style={{ fontSize: 12, color: color.textSecondary }}>
              {c.peer_address} · {c.channel}
              {!c.customer_name && ' · not matched to a customer record'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: space(1) }}>
            {c.handling !== 'HUMAN' && (
              <Btn size="sm" onClick={() => act('take-over')} disabled={busy}>
                Take over
              </Btn>
            )}
            {c.handling !== 'AI' && (
              <Btn size="sm" variant="secondary" disabled={busy}
                   onClick={() => act('hand-back', { reason: 'Handed back.' })}>
                Give back to agent
              </Btn>
            )}
            {c.handling !== 'PAUSED' && (
              <Btn size="sm" variant="secondary" disabled={busy}
                   onClick={() => act('pause', { reason: 'Parked.' })}>
                Pause
              </Btn>
            )}
          </div>
        </div>
      </div>

      <HandlingBand conv={c} />

      {!c.window?.open && (
        <div style={{ marginBottom: space(1.5) }}>
          <Banner tone="warning" title="Outside the 24-hour window">
            {c.window?.detail} This is WhatsApp&apos;s rule, not ours — a
            free-form message sent now will not be delivered.
          </Banner>
        </div>
      )}

      <div style={{
        flex: 1, overflowY: 'auto', background: color.bg, padding: space(1.5),
        borderRadius: radius.md, border: `1px solid ${color.border}`,
        minHeight: 240,
      }}>
        {data.messages.length === 0 && (
          <div style={{ color: color.textSecondary, fontSize: 13 }}>
            Nothing has been said yet.
          </div>
        )}
        {data.messages.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>

      <div style={{ marginTop: space(1.5) }}>
        {willTakeOver && (
          <div style={{
            fontSize: 12, color: color.textSecondary, marginBottom: 6,
          }}>
            Sending a message takes this conversation over. The agent will stop
            replying to this customer until you hand it back.
          </div>
        )}
        <div style={{ display: 'flex', gap: space(1) }}>
          <textarea
            style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
            placeholder="Write a reply…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send();
            }}
          />
          <Btn onClick={send} disabled={busy || !body.trim()}>Send</Btn>
        </div>
      </div>

      {data.events?.length > 0 && (
        <details style={{ marginTop: space(1.5) }}>
          <summary style={{
            cursor: 'pointer', fontSize: 12.5, color: color.textSecondary,
          }}>
            Who has been answering this conversation ({data.events.length})
          </summary>
          <div style={{ marginTop: space(1) }}>
            {data.events.map((e, i) => (
              <div key={i} style={{
                fontSize: 12, color: color.textSecondary, padding: '3px 0',
              }}>
                <strong>{e.event}</strong>
                {e.actor_name ? ` by ${e.actor_name}` : ''}
                {e.reason ? ` — ${e.reason}` : ''}
                {' · '}{when(e.created_at)}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function ConversationInbox({ notify }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const q = filter === 'ALL' ? '' : `&handling=${filter}`;
      const data = await req(`/api/conversations?status=OPEN${q}`);
      setRows(data);
      if (!selected && data.length) setSelected(data[0].id);
    } catch (e) { setErr(e.message); }
  }, [filter, selected]);

  useEffect(() => { load(); }, [load]);

  // Refreshed on a timer because the other party is a customer, not a click.
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (err) return <ErrorBox msg={err} />;
  if (!rows) return <SkeletonCards n={4} />;

  const waiting = rows.filter((r) => r.handling === 'PAUSED').length;

  return (
    <div>
      {waiting > 0 && (
        <div style={{ marginBottom: space(2) }}>
          <Banner tone="warning" title={`${waiting} conversation${waiting === 1 ? '' : 's'} waiting for a person`}>
            The agent stopped answering these because the customer asked for a
            human, raised a complaint, or mentioned a refund. Nothing is being
            sent on them until somebody takes over.
          </Banner>
        </div>
      )}

      <div style={{ display: 'flex', gap: space(1), marginBottom: space(2), flexWrap: 'wrap' }}>
        {[['ALL', 'All open'], ['PAUSED', 'Waiting for a person'],
          ['HUMAN', 'Held by staff'], ['AI', 'Agent answering']].map(([k, label]) => (
          <Btn key={k} size="sm" variant={filter === k ? 'primary' : 'secondary'}
               onClick={() => setFilter(k)}>{label}</Btn>
        ))}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr',
        gap: space(2), alignItems: 'start',
      }}>
        <Card pad={0}>
          <div style={{ maxHeight: 620, overflowY: 'auto' }}>
            {rows.length === 0 && (
              <div style={{ padding: space(2), color: color.textSecondary, fontSize: 13 }}>
                No open conversations.
              </div>
            )}
            {rows.map((r) => (
              <div key={r.id} onClick={() => setSelected(r.id)}
                   style={{
                     padding: space(1.5), cursor: 'pointer',
                     borderBottom: `1px solid ${color.border}`,
                     background: selected === r.id ? `${color.medical}0D` : '#fff',
                   }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  gap: space(1), alignItems: 'baseline',
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: color.text }}>
                    {r.customer_name || r.display_name || r.peer_address}
                  </div>
                  <div style={{ fontSize: 11, color: color.textMuted }}>
                    {when(r.last_message_at)}
                  </div>
                </div>
                <div style={{
                  fontSize: 12.5, color: color.textSecondary, marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {r.last_author === 'CUSTOMER' ? '' : '↩ '}{r.last_body || '—'}
                </div>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Chip tone={r.handling === 'HUMAN' ? 'success'
                    : r.handling === 'PAUSED' ? 'warning' : 'info'}>
                    {r.handling === 'HUMAN' ? r.assigned_to_name
                      : r.handling === 'PAUSED' ? 'Needs a person' : 'AI agent'}
                  </Chip>
                  {r.unread_count > 0 && <Chip tone="danger">{r.unread_count} new</Chip>}
                  {!r.window?.open && <Chip tone="neutral">window shut</Chip>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          {selected
            ? <Thread id={selected} onChanged={load} notify={notify} />
            : <div style={{ color: color.textSecondary, fontSize: 13 }}>
                Pick a conversation.
              </div>}
        </Card>
      </div>
    </div>
  );
}
